import type { Worm } from '../entities/worm';
import type { Mine } from '../weapons/mine';
import type { OilBarrel } from '../entities/oilBarrel';
import type { Sheep } from '../weapons/sheep';
import type { SuperSheep } from '../weapons/superSheep';
import type { MiniRaon } from '../weapons/raon';
import { WA_PHYSICS } from './constants';
import { isRopeActive, releaseRope } from '../weapons/ninjaRope';

export interface WorldStabilityDeps {
  entityRegistry: {
    count: (type: any) => number;
    getAll: (type: any) => any[];
  };
  getWorms: () => Worm[];
  terrain: { isSolid: (x: number, y: number) => boolean };
  networkManager?: { broadcastGameEvent: (event: any) => void };
  turnManager: {
    clearDamageQueue: () => void;
    resetForceStableAttempts: () => void;
  };
  state: { finishDamageResolution: () => void };
  setCameraMode: (mode: string) => void;
}

interface WorldSnapshot {
  worms: Map<string, { x: number; y: number }>;
  projectiles: number;
}

export class WorldStabilityCheckerImpl {
  private lastWorldStateSnapshot: WorldSnapshot | null = null;
  private forceStableAttempts = 0;
  private readonly STUCK_POSITION_THRESHOLD = 5.0;
  private readonly VELOCITY_THRESHOLD = 5;
  private readonly MAX_GROUND_SEARCH = 500;

  constructor(private deps: WorldStabilityDeps) {}

  isWorldStable(): boolean {
    const hasActiveProjectiles =
      this.deps.entityRegistry.count('projectile') > 0 ||
      this.deps.entityRegistry.count('homingMissile') > 0;
    if (hasActiveProjectiles) return false;

    const allWormsSettled = this.deps
      .getWorms()
      .every(
        (w) =>
          w.state === 'dead' ||
          w.state === 'drowning' ||
          (!isRopeActive(w) &&
            w.isGrounded &&
            Math.abs(w.velocity.x) < this.VELOCITY_THRESHOLD &&
            Math.abs(w.velocity.y) < this.VELOCITY_THRESHOLD),
      );
    if (!allWormsSettled) return false;

    const allMinesSettled = (this.deps.entityRegistry.getAll('mine') as Mine[]).every(
      (m) =>
        !m.active ||
        (m.isGrounded &&
          Math.abs(m.velocity.x) < this.VELOCITY_THRESHOLD &&
          Math.abs(m.velocity.y) < this.VELOCITY_THRESHOLD),
    );
    if (!allMinesSettled) return false;

    const allBarrelsSettled = (this.deps.entityRegistry.getAll('barrel') as OilBarrel[]).every(
      (b) =>
        !b.active ||
        (b.isGrounded &&
          Math.abs(b.velocity.x) < this.VELOCITY_THRESHOLD &&
          Math.abs(b.velocity.y) < this.VELOCITY_THRESHOLD),
    );
    if (!allBarrelsSettled) return false;

    const allSheepSettled = (this.deps.entityRegistry.getAll('sheep') as Sheep[]).every(
      (s) =>
        !s.active ||
        (s.isGrounded &&
          Math.abs(s.velocity.x) < this.VELOCITY_THRESHOLD &&
          Math.abs(s.velocity.y) < this.VELOCITY_THRESHOLD),
    );
    if (!allSheepSettled) return false;

    const allSuperSheepSettled = (
      this.deps.entityRegistry.getAll('superSheep') as SuperSheep[]
    ).every(
      (ss) =>
        !ss.active ||
        (ss.isGrounded &&
          Math.abs(ss.velocity.x) < this.VELOCITY_THRESHOLD &&
          Math.abs(ss.velocity.y) < this.VELOCITY_THRESHOLD),
    );
    if (!allSuperSheepSettled) return false;

    const allRaonsSettled = (this.deps.entityRegistry.getAll('raon') as MiniRaon[]).every(
      (r) =>
        !r.active ||
        (r.isGrounded &&
          Math.abs(r.velocity.x) < this.VELOCITY_THRESHOLD &&
          Math.abs(r.velocity.y) < this.VELOCITY_THRESHOLD),
    );
    if (!allRaonsSettled) return false;

    return true;
  }

  isWorldStuck(): boolean {
    if (!this.lastWorldStateSnapshot) return false;

    for (const worm of this.deps.getWorms()) {
      if (worm.state === 'dead' || worm.state === 'drowning') continue;

      const lastPos = this.lastWorldStateSnapshot.worms.get(worm.id);
      if (!lastPos) continue;

      const dx = Math.abs(worm.position.x - lastPos.x);
      const dy = Math.abs(worm.position.y - lastPos.y);
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > this.STUCK_POSITION_THRESHOLD) {
        return false;
      }
    }

    const currentProjectileCount =
      this.deps.entityRegistry.count('projectile') +
      this.deps.entityRegistry.count('homingMissile') +
      this.deps.entityRegistry.count('sheep') +
      this.deps.entityRegistry.count('superSheep') +
      this.deps.entityRegistry.count('raon');

    if (currentProjectileCount !== this.lastWorldStateSnapshot.projectiles) {
      return false;
    }

    return true;
  }

  forceWorldStable(fromNetwork = false): void {
    this.forceStableAttempts++;
    console.warn(
      `[VVorms] Forcing world stable - attempt ${this.forceStableAttempts}${fromNetwork ? ' (from network)' : ''}`,
    );

    if (!fromNetwork && this.deps.networkManager) {
      this.deps.networkManager.broadcastGameEvent({ type: 'forceStable' });
    }

    if (this.forceStableAttempts >= 3) {
      console.warn('[VVorms] Too many force-stable attempts, skipping to next turn');
      this.deps.turnManager.clearDamageQueue();
      for (const worm of this.deps.getWorms()) {
        if (worm.state !== 'dead' && worm.state !== 'drowning') {
          if (isRopeActive(worm)) {
            releaseRope(worm);
          }
          worm.clearPendingDamage();
          worm.velocity.x = 0;
          worm.velocity.y = 0;
          worm.isGrounded = true;
          worm.state = 'idle';
        }
      }
      if (!fromNetwork) {
        this.deps.state.finishDamageResolution();
      }
      this.deps.setCameraMode('worm');
      this.deps.turnManager.resetForceStableAttempts();
      return;
    }

    for (const worm of this.deps.getWorms()) {
      if (worm.state === 'dead' || worm.state === 'drowning') continue;

      if (isRopeActive(worm)) {
        releaseRope(worm);
      }

      if (
        !worm.isGrounded ||
        worm.state === 'falling' ||
        worm.state === 'jumping' ||
        worm.state === 'backflipping'
      ) {
        worm.velocity.x = 0;
        worm.velocity.y = 0;

        const groundY = this.findGroundBelow(
          worm.position.x + WA_PHYSICS.WORM.WIDTH / 2,
          worm.position.y + WA_PHYSICS.WORM.HEIGHT,
        );
        if (groundY !== null) {
          worm.position.y = groundY - WA_PHYSICS.WORM.HEIGHT;
          worm.isGrounded = true;
          worm.state = 'idle';
        } else {
          worm.isGrounded = false;
          worm.state = 'falling';
        }
      }
    }

    for (const mine of this.deps.entityRegistry.getAll('mine') as Mine[]) {
      if (!mine.active) continue;
      if (
        !mine.isGrounded ||
        Math.abs(mine.velocity.x) >= this.VELOCITY_THRESHOLD ||
        Math.abs(mine.velocity.y) >= this.VELOCITY_THRESHOLD
      ) {
        mine.velocity.x = 0;
        mine.velocity.y = 0;

        const groundY = this.findGroundBelow(mine.position.x, mine.position.y + 1);
        if (groundY !== null) {
          mine.position.y = groundY - 1;
          mine.isGrounded = true;
        } else {
          mine.isGrounded = true;
        }
      }
    }

    for (const barrel of this.deps.entityRegistry.getAll('barrel') as OilBarrel[]) {
      if (!barrel.active) continue;
      if (
        !barrel.isGrounded ||
        Math.abs(barrel.velocity.x) >= this.VELOCITY_THRESHOLD ||
        Math.abs(barrel.velocity.y) >= this.VELOCITY_THRESHOLD
      ) {
        barrel.velocity.x = 0;
        barrel.velocity.y = 0;

        const groundY = this.findGroundBelow(barrel.position.x, barrel.position.y + 1);
        if (groundY !== null) {
          barrel.position.y = groundY - 1;
          barrel.isGrounded = true;
        } else {
          barrel.isGrounded = true;
        }
      }
    }
  }

  captureWorldSnapshot(): void {
    const wormPositions = new Map<string, { x: number; y: number }>();
    for (const worm of this.deps.getWorms()) {
      if (worm.state !== 'dead' && worm.state !== 'drowning') {
        wormPositions.set(worm.id, { x: worm.position.x, y: worm.position.y });
      }
    }

    const projectileCount =
      this.deps.entityRegistry.count('projectile') +
      this.deps.entityRegistry.count('homingMissile') +
      this.deps.entityRegistry.count('sheep') +
      this.deps.entityRegistry.count('superSheep') +
      this.deps.entityRegistry.count('raon');

    this.lastWorldStateSnapshot = {
      worms: wormPositions,
      projectiles: projectileCount,
    };
  }

  findGroundBelow(x: number, startY: number): number | null {
    for (let dy = 0; dy < this.MAX_GROUND_SEARCH; dy++) {
      if (this.deps.terrain.isSolid(x, startY + dy)) {
        return startY + dy;
      }
    }
    return null;
  }

  resetForceStableAttempts(): void {
    this.forceStableAttempts = 0;
  }
}
